import {
  Box,
  Button,
  CardMedia,
  Checkbox,
  Input,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Step,
  StepLabel,
  Stepper,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import FormControlLabel from "@mui/material/FormControlLabel";
import { useNavigate } from "react-router-dom";
import AuthLayout from "./AuthLayout";
import React, { useEffect, useState } from "react";

import { Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AIStyles from "../../Modals/AIstyles";
import SelectUpload from "../../Modals/select_upload";
import { useFormik } from "formik";
import * as Yup from "yup";
import { showError, showToast } from "../../constants/toasts";
import { useUpdateProfileMutation } from "../../service/auth";
import { VENDOR_PROFILE_STEPS } from "../../utils/enums";
import { useLazyGetProfileQuery } from "../../service/auth";
import { getFromStorage, STORAGE_KEYS } from "../../helpers";
import { User } from "../../types/User";
import { VendorStore } from "../../types/General";
import PhoneInput from "react-phone-input-2";
import { UploadMedia } from "../../utils/mediaUpload";
import { GridCloseIcon } from "@mui/x-data-grid";
import Loader from "../../constants/Loader";

const steps = [
  "Financial Setup",
  "Store Policies",
  "Shipping & Logistics",
  "Delivery & Return Settings",
  "Store Branding",
  "Subscription Plan",
];
const careInstructionTemplates = {
  fashion: [
    "Hand wash with cold water. Do not bleach.",
    "Dry flat in shade. Avoid machine drying.",
  ],
  homeFragrance: [
    "Keep away from direct sunlight. Store in a cool area.",
    "Avoid placing near heat sources. Keep bottle tightly sealed.",
  ],
  accessories: [
    "Avoid contact with water and perfumes.",
    "Store in a dry place. Keep away from sharp objects.",
  ],
};
const pickupInstructionOptions = [
  "Call before arrival",
  "Leave at reception",
  "Ring doorbell",
  "Parking available",
  "Custom instruction",
];

const storeMottoTemplates = [
  "Crafted with Care",
  "Made in the UAE",
  "Celebrate Local Creativity",
  // Admin can add more here
];

const storeStoryTemplates = [
  "Born from a passion for craftsmanship, our store celebrates local artisans and the beauty of handmade traditions...",
  "Inspired by UAE heritage, our brand blends modern aesthetics with timeless cultural values...",
  "Built by creators for creators, our store is a home for unique products made with soul and purpose...",
];

const ALL_PLACES = [
  "Dubai",
  "Abu Dhabi",
  "Sharjah / Ajman",
  "RAK",
  "Fujairah",
  "UAQ",
];

const SubscriptionForm = () => {
  const navigate = useNavigate();
  const [updateProfile] = useUpdateProfileMutation();
  const [getProfile] = useLazyGetProfileQuery();
  const userData = getFromStorage(STORAGE_KEYS.userData);
  const parsedUserData: User | null = userData
    ? (JSON.parse(userData) as User)
    : null;
  const [openAIModal, setOpenAIModal] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [fastDeliveryEnabled, setFastDeliveryEnabled] = useState(false);
  const [vendorData, setVendorData] = useState<VendorStore | null>(null);
  const [pickupOption, setPickupOption] = useState("");
  const [selectedInstruction, setSelectedInstruction] = useState("");
  const [customInstruction, setCustomInstruction] = useState("");
  const [banner, setBanner] = useState("");
  const [deliverySLA, setDeliverySLA] = useState("1");
  const [selectedMotto, setSelectedMotto] = useState("");
  const [customMotto, setCustomMotto] = useState("");
  const [storeStory, setStoreStory] = useState("");
  const [showStoryEditor, setShowStoryEditor] = useState(false);
  const [selectedCommitments, setSelectedCommitments] = useState<string[]>([]);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isOtpVerified, setIsOtpVerified] = useState(false);
  const [selectedTime, setSelectedTime] = useState("15:00");
  const [scope, setScope] = useState<"all" | "specific">("all");
  const [places, setPlaces] = useState<string[]>(ALL_PLACES);

  const [openModal, setOpenModal] = useState(false);
  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handleChange =
    (label: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.checked) {
        setSelectedCommitments((prev) => [...prev, label]);
      } else {
        setSelectedCommitments((prev) => prev.filter((item) => item !== label));
      }
    };

  const selectedCategory = "fashion"; // fashion | homeFragrance | accessories

  const handleProfileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    const file = files?.length ? files[0] : null;

    if (!file) {
      setBanner("");
      return;
    }

    const imageUrl = (await UploadMedia(file)) as any;
    if (imageUrl?.data) {
      setBanner(imageUrl?.data || "");
    }
  };

  const BankFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      accountName: vendorData?.bankDetails?.accountName || "",
      bankName: vendorData?.bankDetails?.bankName || "",
      iban: vendorData?.bankDetails?.iban || "",
      swift: vendorData?.bankDetails?.swift || "",
      branch: vendorData?.bankDetails?.branch || "",
    },
    validationSchema: Yup.object({
      accountName: Yup.string().required("Holder's name is required"),
      bankName: Yup.string().required("Bank name is required"),
      iban: Yup.string().required("IBAN is required"),
    }),
    onSubmit: async (values: any) => {
      BankFormik.setSubmitting(true);

      const body = {
        bankDetails: {
          accountName: values.accountName || "",
          bankName: values.bankName || "",
          iban: values.iban || "",
          swift: values.swift || "",
          branch: values.branch || "",
          document: "",
        },
        step: VENDOR_PROFILE_STEPS.BANK,
      };

      try {
        setLoading(true);
        const response = await updateProfile(body).unwrap();
        setLoading(false);
        if (response?.statusCode === 200) {
          showToast(response?.message || "");
          setActiveStep((prev) => prev + 1);
        } else {
          showError(response?.message || "");
        }
      } catch (error: any) {
        setLoading(false);
        showError(error?.data?.message || "");
        console.error(error);
      }
    },
  });

  const StorePolicyFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      storeCareInfo: vendorData?.storeCareInfo || "",
    },
    validationSchema: Yup.object({
      storeCareInfo: Yup.string().required("Care info is required"),
    }),
    onSubmit: async (values: any) => {
      BankFormik.setSubmitting(true);

      if (!selectedCommitments.length) {
        showError("Please select at least one commitment");
        return;
      }

      const body = {
        storeCareInfo: values.storeCareInfo,
        customerTrustInfo: selectedCommitments,
        step: VENDOR_PROFILE_STEPS.POLICIES,
      };

      try {
        setLoading(true);
        const response = await updateProfile(body).unwrap();
        setLoading(false);
        if (response?.statusCode === 200) {
          showToast(response?.message || "");
          setActiveStep((prev) => prev + 1);
        } else {
          showError(response?.message || "");
        }
      } catch (error: any) {
        setLoading(false);
        showError(error?.data?.message || "");
        console.error(error);
      }
    },
  });

  const ShippingFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      shippingAddress: vendorData?.shippingAddress?.detail || "",
      contactPhone: vendorData?.shippingContact?.phone || "",
      contactCountryCode: vendorData?.shippingContact?.countryCode || "",
      allowNonReturnableProducts:
        vendorData?.allowNonReturnableProducts || false,
      pickupInstructions: vendorData?.pickupInstructions || "",
    },
    validationSchema: Yup.object({
      shippingAddress: Yup.string().required("Shipping Address is required"),
    }),
    onSubmit: async (values: any) => {
      BankFormik.setSubmitting(true);

      const body = {
        shippingAddress: {
          detail: values.shippingAddress,
        },
        shippingContact: {
          name: parsedUserData?.name || "",
          phone: values.contactPhone || "",
          countryCode: values.contactCountryCode || "+971",
        },
        pickupInstructions: values.pickupInstructions,
        allowNonReturnableProducts: values.allowNonReturnableProducts,
        step: VENDOR_PROFILE_STEPS.SHIPPING,
      };

      try {
        setLoading(true);
        const response = await updateProfile(body).unwrap();
        setLoading(false);
        if (response?.statusCode === 200) {
          showToast(response?.message || "");
          setActiveStep((prev) => prev + 1);
        } else {
          showError(response?.message || "");
        }
      } catch (error: any) {
        setLoading(false);
        showError(error?.data?.message || "");
        console.error(error);
      }
    },
  });

  const handleScopeChange = (e: any) => {
    const value = e.target.value;
    setScope(value);

    if (value === "all") {
      setPlaces(ALL_PLACES); // select all
    } else {
      setPlaces([]); // reset for manual selection
    }
  };

  const handlePlaceToggle = (place: string) => {
    setPlaces((prev) =>
      prev.includes(place) ? prev.filter((p) => p !== place) : [...prev, place]
    );
  };

  const convertToUTC = (time: string) => {
    const [hours, minutes] = time.split(":").map(Number);

    const date = new Date();
    date.setHours(hours, minutes, 0, 0);

    return date.toISOString(); // UTC
  };

  const DeliveryFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      returnAddress: vendorData?.returnAddress?.detail || "",
      sameAsPickup: false,
      returnPolicyType: vendorData?.returnWithIn || "Standard Return – 7 days",
      returnPolicyValue: vendorData?.returnWithIn || "Standard Return – 7 days",
      isSameDayDelivery: vendorData?.isSameDayDelivery || false,
    },
    validationSchema: Yup.object({
      returnAddress: Yup.string().required("Return address is required"),
      returnPolicyValue: Yup.string().required(
        "Return policy value is required"
      ),
    }),
    onSubmit: async (values: any) => {
      BankFormik.setSubmitting(true);

      if (!deliverySLA) {
        showError("Delivery SLA is required");
        return;
      }

      if (fastDeliveryEnabled && !selectedTime) {
        return;
      }

      if (fastDeliveryEnabled && !places?.length) {
        showError("Please select Region Availability");
        return;
      }

      const body = {
        deliverySLA: Number(deliverySLA),
        enableFastDelivery: fastDeliveryEnabled,
        ...(fastDeliveryEnabled
          ? { isSameDayDelivery: values.isSameDayDelivery }
          : {}),
        ...(fastDeliveryEnabled
          ? { cutOffTime: convertToUTC(selectedTime) }
          : {}),
        ...(fastDeliveryEnabled ? { regionsAvailable: places } : {}),
        returnWithIn: values.returnPolicyValue,
        returnAddress: {
          detail: values.returnAddress,
        },
        step: VENDOR_PROFILE_STEPS.RETURN,
      };

      try {
        setLoading(true);
        const response = await updateProfile(body).unwrap();
        setLoading(false);
        if (response?.statusCode === 200) {
          showToast(response?.message || "");
          setActiveStep((prev) => prev + 1);
          setError(false);
        } else {
          showError(response?.message || "");
        }
      } catch (error: any) {
        setLoading(false);
        showError(error?.data?.message || "");
        console.error(error);
      }
    },
  });

  const BrandFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      motto: vendorData?.storeMotto || "",
      story: vendorData?.storeStory || "",
    },
    validationSchema: Yup.object({
      motto: Yup.string().required("Store Motto is required"),
      story: Yup.string().required("Store Story is required"),
    }),
    onSubmit: async (values: any) => {
      BankFormik.setSubmitting(true);

      if (!banner) {
        showError("Please upload a banner image");
        return;
      }

      const body = {
        storeBanner: banner,
        storeMotto: values.motto,
        storeStory: values.story,
        step: VENDOR_PROFILE_STEPS.BRANDING,
      };

      try {
        setLoading(true);
        const response = await updateProfile(body).unwrap();
        setLoading(false);
        if (response?.statusCode === 200) {
          showToast(response?.message || "");
          setActiveStep((prev) => prev + 1);
        } else {
          showError(response?.message || "");
        }
      } catch (error: any) {
        setLoading(false);
        showError(error?.data?.message || "");
        console.error(error);
      }
    },
  });

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const body = {
        step: VENDOR_PROFILE_STEPS.SUBSCRIPTION,
        isProfileComplete: true,
      };

      setLoading(true);
      const response = await updateProfile(body).unwrap();
      setLoading(false);
      if (response?.statusCode === 200) {
        showToast(response?.message || "");
        navigate("/dashboard");
      } else {
        showError(response?.message || "");
      }
    } catch (error: any) {
      setLoading(false);
      showError(error?.data?.message || "");
    }
  };

  const handleChangePhone = (phone: any, country: any) => {
    ShippingFormik.setFieldValue(
      "contactPhone",
      phone?.replace(country.dialCode, "")
    );
    ShippingFormik.setFieldValue("contactCountryCode", country?.dialCode);
  };

  const { setFieldValue, values } = ShippingFormik;

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <form className="form" onSubmit={BankFormik.handleSubmit}>
            <div className="gap_p">
              {/* Account Holder Name */}
              <div className="control_group w_50">
                <label htmlFor="accountHolder">Account Holder’s Name</label>
                <TextField
                  id="accountName"
                  name="accountName"
                  placeholder="Enter Account Holder’s Name"
                  fullWidth
                  className="text_field"
                  value={BankFormik.values.accountName}
                  onChange={(val) => {
                    if (val.target.value === " " || val.target.value === ".") {
                    } else {
                      BankFormik.handleChange(val);
                    }
                  }}
                  onBlur={BankFormik.handleBlur}
                  helperText={
                    BankFormik.touched.accountName &&
                    typeof BankFormik.errors.accountName === "string"
                      ? BankFormik.errors.accountName
                      : ""
                  }
                />
              </div>

              {/* Bank */}
              <div className="control_group w_50">
                <label htmlFor="bankName">Bank Name</label>
                <TextField
                  id="bankName"
                  name="bankName"
                  placeholder="Enter Account Holder’s Name"
                  fullWidth
                  className="text_field"
                  value={BankFormik.values.bankName}
                  onChange={(val) => {
                    if (val.target.value === " " || val.target.value === ".") {
                    } else {
                      BankFormik.handleChange(val);
                    }
                  }}
                  onBlur={BankFormik.handleBlur}
                  helperText={
                    BankFormik.touched.bankName &&
                    typeof BankFormik.errors.bankName === "string"
                      ? BankFormik.errors.bankName
                      : ""
                  }
                />
              </div>

              {/* IBAN */}
              <div className="control_group w_50">
                <label htmlFor="iban">IBAN Number</label>
                <TextField
                  id="iban"
                  name="iban"
                  placeholder="Enter Number"
                  fullWidth
                  className="text_field"
                  value={BankFormik.values.iban}
                  onChange={(val) => {
                    if (val.target.value === " " || val.target.value === ".") {
                    } else {
                      BankFormik.handleChange(val);
                    }
                  }}
                  onBlur={BankFormik.handleBlur}
                  helperText={
                    BankFormik.touched.iban &&
                    typeof BankFormik.errors.iban === "string"
                      ? BankFormik.errors.iban
                      : ""
                  }
                />
              </div>

              {/* --- ADVANCED OPTIONS ACCORDION --- */}
              <div className="control_group w_100 accordions_drop">
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <strong>Advanced Options</strong>
                  </AccordionSummary>

                  <AccordionDetails>
                    <div
                      className="gap_p"
                      style={{ display: "flex", flexWrap: "wrap" }}
                    >
                      {/* Swift / BIC Code */}
                      <div className="control_group w_50">
                        <label htmlFor="swiftCode">
                          Swift/BIC Code (Optional)
                        </label>
                        <TextField
                          id="swift"
                          name="swift"
                          placeholder="Enter Code"
                          fullWidth
                          className="text_field"
                          value={BankFormik.values.swift}
                          onChange={(val) => {
                            if (
                              val.target.value === " " ||
                              val.target.value === "."
                            ) {
                            } else {
                              BankFormik.handleChange(val);
                            }
                          }}
                          onBlur={BankFormik.handleBlur}
                          helperText={
                            BankFormik.touched.swift &&
                            typeof BankFormik.errors.swift === "string"
                              ? BankFormik.errors.swift
                              : ""
                          }
                        />
                      </div>

                      {/* Bank Branch */}
                      <div className="control_group w_50">
                        <label htmlFor="bankBranch">
                          Bank Branch (Optional)
                        </label>
                        <TextField
                          id="branch"
                          name="branch"
                          placeholder="Enter Branch Name"
                          fullWidth
                          className="text_field"
                          value={BankFormik.values.branch}
                          onChange={(val) => {
                            if (
                              val.target.value === " " ||
                              val.target.value === "."
                            ) {
                            } else {
                              BankFormik.handleChange(val);
                            }
                          }}
                          onBlur={BankFormik.handleBlur}
                          helperText={
                            BankFormik.touched.branch &&
                            typeof BankFormik.errors.branch === "string"
                              ? BankFormik.errors.branch
                              : ""
                          }
                        />
                      </div>
                    </div>
                  </AccordionDetails>
                </Accordion>
              </div>
            </div>

            <div className="btn_flex">
              <Button
                className="btn btn_grey"
                onClick={handleBack}
                disabled={activeStep === 0}
              >
                Back
              </Button>

              <Button className="btn btn_primary" type="submit">
                Next
              </Button>
            </div>
          </form>
        );
      case 1:
        return (
          <form className="form" onSubmit={StorePolicyFormik.handleSubmit}>
            <div className="gap_p return_policy">
              {/* ------------------ C. STORE CARE INSTRUCTIONS ------------------ */}
              <div className="control_group w_100 ai_rec_btn">
                <label htmlFor="careInstructions">
                  Store Care Instructions (Optional)
                  <img src="/static/images/ai_icon.svg" alt="icon" />
                </label>

                <Select fullWidth value={"default"} className="select_abs">
                  <MenuItem disabled value="default">
                    Select Template
                  </MenuItem>
                  {careInstructionTemplates[selectedCategory]?.map(
                    (template, index) => (
                      <MenuItem value="All"> {template}</MenuItem>
                    )
                  )}
                </Select>

                <TextField
                  id="storeCareInfo"
                  name="storeCareInfo"
                  placeholder="Enter"
                  fullWidth
                  multiline
                  className="text_field"
                  value={StorePolicyFormik.values.storeCareInfo}
                  onChange={(val) => {
                    if (val.target.value === " " || val.target.value === ".") {
                    } else {
                      StorePolicyFormik.handleChange(val);
                    }
                  }}
                  onBlur={StorePolicyFormik.handleBlur}
                  helperText={
                    StorePolicyFormik.touched.storeCareInfo &&
                    typeof StorePolicyFormik.errors.storeCareInfo === "string"
                      ? StorePolicyFormik.errors.storeCareInfo
                      : ""
                  }
                />

                {/* Optional AI Button */}
                <Button className="btn btn_primary sm" type="button">
                  <img src="/static/images/ai_icon.svg" alt="" />
                  Enhance with AI
                </Button>
              </div>

              {/* ------------------ D. CUSTOMER TRUST COMMITMENT ------------------ */}
              <div className="control_group w_100">
                <label>Customer Trust Commitment</label>
              </div>

              <div className="control_group">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedCommitments.includes(
                        "Authenticity Guaranteed"
                      )}
                      onChange={handleChange("Authenticity Guaranteed")}
                    />
                  }
                  label="Authenticity Guaranteed"
                />
              </div>

              <div className="control_group">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedCommitments.includes("Secure Payments")}
                      onChange={handleChange("Secure Payments")}
                    />
                  }
                  label="Secure Payments"
                />
              </div>

              <div className="control_group">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedCommitments.includes(
                        "Transparent Returns"
                      )}
                      onChange={handleChange("Transparent Returns")}
                    />
                  }
                  label="Transparent Returns"
                />
              </div>

              <div className="control_group">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedCommitments.includes("Privacy Policy")}
                      onChange={handleChange("Privacy Policy")}
                    />
                  }
                  label="Privacy Policy"
                />
              </div>
            </div>

            <div className="btn_flex">
              <Button
                className="btn btn_grey"
                onClick={handleBack}
                disabled={activeStep === 0}
              >
                Back
              </Button>

              <Button className="btn btn_primary" type="submit">
                Next
              </Button>
            </div>
          </form>
        );
      case 2:
        return (
          <form className="form" onSubmit={ShippingFormik.handleSubmit}>
            <Box
              sx={{ border: "1px solid #ddd", borderRadius: 2, p: 2, mb: 3 }}
            >
              <div className="form">
                <div className="gap_p">
                  <div className="title_inner">
                    <h3>Integrated Shipping</h3>
                  </div>

                  {/* ---------------- PRIMARY PICKUP ADDRESS ---------------- */}
                  <div className="control_group w_100">
                    <label>Primary Pickup Address</label>

                    <div className="pickup_address_options">
                      {/* Option 1 */}
                      <FormControlLabel
                        control={
                          <Radio
                            checked={pickupOption === "license"}
                            onChange={() => setPickupOption("license")}
                          />
                        }
                        label="Use License Address (auto-fill)"
                      />

                      {/* Option 2 */}
                      <FormControlLabel
                        control={
                          <Radio
                            checked={pickupOption === "personal"}
                            onChange={() => setPickupOption("personal")}
                          />
                        }
                        label="Use Personal Address (from profile)"
                      />

                      {/* Option 3 */}
                      <FormControlLabel
                        control={
                          <Radio
                            checked={pickupOption === "new"}
                            onChange={() => setPickupOption("new")}
                          />
                        }
                        label="Enter New Address (Google Maps autocomplete)"
                      />
                    </div>

                    {/* If NEW ADDRESS → Show Maps Field */}
                    <TextField
                      id="shippingAddress"
                      name="shippingAddress"
                      placeholder="Address"
                      className="text_field"
                      fullWidth
                      value={ShippingFormik.values.shippingAddress}
                      onChange={(val) => {
                        if (
                          val.target.value === " " ||
                          val.target.value === "."
                        ) {
                        } else {
                          ShippingFormik.handleChange(val);
                        }
                      }}
                      onBlur={ShippingFormik.handleBlur}
                      helperText={
                        ShippingFormik.touched.shippingAddress &&
                        typeof ShippingFormik.errors.shippingAddress ===
                          "string"
                          ? ShippingFormik.errors.shippingAddress
                          : ""
                      }
                    />
                  </div>

                  {/* ---------------- CONTACT PHONE NUMBER ---------------- */}
                  <div className="control_group w_50">
                    <label>Contact Phone Number</label>
                    <PhoneInput
                      enableSearch
                      value={
                        ShippingFormik.values.contactCountryCode +
                        ShippingFormik.values.contactPhone
                      }
                      country={"uae"}
                      inputClass="phoneInput"
                      onChange={(phone, country) =>
                        handleChangePhone(phone, country)
                      }
                      inputStyle={{ width: "100%" }}
                      buttonClass="phoneBtn"
                      placeholder="Phone Number"
                    />
                    <p className="err_msg">
                      {error && !ShippingFormik.values.contactPhone?.length
                        ? "Phone number is required"
                        : ""}
                    </p>
                  </div>

                  {/* ---------------- PICKUP INSTRUCTIONS ---------------- */}
                  <div className="control_group w_100">
                    <label>Pickup Instructions</label>

                    <div className="instruction_chips">
                      {pickupInstructionOptions.map((option, i) => (
                        <button
                          key={i}
                          type="button"
                          className={`instruction_chip ${
                            selectedInstruction === option ? "selected" : ""
                          }`}
                          onClick={() => {
                            setSelectedInstruction(option);

                            if (option !== "Custom instruction") {
                              setCustomInstruction("");
                              setFieldValue("pickupInstructions", option); // ✅ SET HERE
                            } else {
                              setFieldValue("pickupInstructions", ""); // clear for custom input
                            }
                          }}
                        >
                          {option}
                        </button>
                      ))}
                    </div>

                    {/* custom input appears only when selected */}
                    {selectedInstruction === "Custom instruction" && (
                      <TextField
                        placeholder="Enter your custom instruction"
                        fullWidth
                        sx={{ mt: 2 }}
                        value={customInstruction}
                        onChange={(e) => {
                          const value = e.target.value;
                          setCustomInstruction(value);
                          setFieldValue("pickupInstructions", value); // ✅ SET HERE
                        }}
                      />
                    )}
                  </div>
                </div>
              </div>
            </Box>

            <div className="gap_p">
              <div className="title_inner">
                <h3>Allow Non-Returnable Products</h3>
              </div>
              <div className="control_group">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={ShippingFormik.values.allowNonReturnableProducts}
                      onChange={(e) =>
                        ShippingFormik.setFieldValue(
                          "allowNonReturnableProducts",
                          e.target.checked
                        )
                      }
                    />
                  }
                  label="For hygiene or customized items only"
                />
              </div>
            </div>

            <div className="btn_flex">
              <Button
                className="btn btn_grey"
                onClick={handleBack}
                disabled={activeStep === 0}
              >
                Back
              </Button>

              <Button
                className="btn btn_primary"
                type="submit"
                onClick={() => setError(true)}
              >
                Next
              </Button>
            </div>
          </form>
        );
      case 3:
        return (
          <form className="form" onSubmit={DeliveryFormik.handleSubmit}>
            <div className="gap_p">
              {/* ========================== DELIVERY SLA ========================== */}
              <Box sx={{ border: "1px solid #ddd", borderRadius: 2, p: 2 }}>
                <div className="form">
                  <div className="gap_p">
                    <div className="title_inner">
                      <h3>Delivery SLA (Seller’s Prep Time)</h3>
                      <p style={{ textAlign: "left" }}>
                        How fast can you prepare an order for pickup?
                      </p>
                    </div>

                    {/* RADIO BUTTONS ONLY */}
                    <RadioGroup
                      value={deliverySLA}
                      onChange={(e) => setDeliverySLA(e.target.value)}
                    >
                      <FormControlLabel
                        value={"1"}
                        control={<Radio />}
                        label="Same-day (24h)"
                      />
                      <FormControlLabel
                        value={"2"}
                        control={<Radio />}
                        label="1–2 days"
                      />
                      <FormControlLabel
                        value={"3"}
                        control={<Radio />}
                        label="3 days"
                      />
                    </RadioGroup>
                  </div>
                </div>
              </Box>

              {/* ========================== FAST DELIVERY ========================== */}
              <Box sx={{ border: "1px solid #ddd", borderRadius: 2, p: 2 }}>
                <div className="form">
                  <div className="gap_p">
                    <div
                      className="title_inner"
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <h3>Enable Fast Delivery</h3>
                      {/* Toggle Switch */}
                      <Switch
                        checked={fastDeliveryEnabled}
                        onChange={(e) =>
                          setFastDeliveryEnabled(e.target.checked)
                        }
                        color="primary"
                      />
                    </div>
                  </div>
                </div>
                {fastDeliveryEnabled && (
                  <Box sx={{ mt: 2, pl: 1 }}>
                    {" "}
                    {/* 1. Delivery Type */}
                    <p style={{ textAlign: "left" }}> 1. Delivery Type </p>
                    <div style={{ marginTop: "6px" }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            name="isSameDayDelivery"
                            checked={DeliveryFormik.values.isSameDayDelivery}
                            onChange={DeliveryFormik.handleChange}
                          />
                        }
                        label="Same-Day Delivery"
                      />
                    </div>{" "}
                    {/* 2. Cut-off time */}{" "}
                    <p className="mt_20" style={{ textAlign: "left" }}>
                      {" "}
                      2. Cut-Off Time{" "}
                    </p>
                    <TextField
                      select
                      size="small"
                      sx={{ mt: 1, width: "200px" }}
                      value={selectedTime}
                      onChange={(e) => setSelectedTime(e.target.value)}
                    >
                      <MenuItem value="12:00">12:00 PM</MenuItem>
                      <MenuItem value="14:00">2:00 PM</MenuItem>
                      <MenuItem value="15:00">3:00 PM</MenuItem>
                      <MenuItem value="16:00">4:00 PM</MenuItem>
                    </TextField>
                    <p className="err_msg">
                      {fastDeliveryEnabled && error && !selectedTime
                        ? "Cut-off time is required"
                        : ""}
                    </p>
                    {/* 3. Region Availability */}
                    <p className="mt_20" style={{ textAlign: "left" }}>
                      {" "}
                      3. Region Availability{" "}
                    </p>
                    <RadioGroup
                      value={scope}
                      onChange={handleScopeChange}
                      sx={{ mt: 1 }}
                    >
                      <FormControlLabel
                        value="all"
                        control={<Radio />}
                        label="All UAE"
                      />
                      <FormControlLabel
                        value="specific"
                        control={<Radio />}
                        label="Specific Emirates"
                      />
                    </RadioGroup>
                    {/* Emirates checkboxes */}
                    <Box sx={{ pl: 3, mt: 1 }}>
                      {ALL_PLACES.map((place) => (
                        <FormControlLabel
                          key={place}
                          control={
                            <Checkbox
                              checked={places.includes(place)}
                              disabled={scope === "all"}
                              onChange={() => handlePlaceToggle(place)}
                            />
                          }
                          label={place}
                        />
                      ))}
                    </Box>
                    {/* 4. Information Box */}{" "}
                    <Box
                      sx={{
                        mt: 3,
                        p: 2,
                        border: "1px solid #e0e0e0",
                        borderRadius: 2,
                        background: "#FFF7E6",
                      }}
                    >
                      <Typography sx={{ fontWeight: 600, mb: 1 }}>
                        {" "}
                        Vendor Responsibility{" "}
                      </Typography>
                      <Typography sx={{ fontSize: "14px", lineHeight: 1.6 }}>
                        {" "}
                        You must have the order ready for pickup on time. <br />{" "}
                        If not ready, fast delivery may be disabled for your
                        store.{" "}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>

              {/* ========================== RETURN MANAGEMENT ========================== */}
              <Box sx={{ border: "1px solid #ddd", borderRadius: 2, p: 2 }}>
                <div className="title_inner">
                  <h3>Return Management</h3>
                </div>
                <div className="gap_p">
                  {/* Return & Exchange Options */}
                  <div className="control_group w_100">
                    <label>Return & Exchange Policy</label>
                    <TextField
                      select
                      fullWidth
                      value={DeliveryFormik.values.returnPolicyType}
                      onChange={(e) => {
                        const value = e.target.value;

                        DeliveryFormik.setFieldValue("returnPolicyType", value);

                        // If not custom → save menu value directly
                        if (value !== "custom") {
                          DeliveryFormik.setFieldValue(
                            "returnPolicyValue",
                            value
                          );
                        } else {
                          DeliveryFormik.setFieldValue("returnPolicyValue", "");
                        }
                      }}
                    >
                      <MenuItem value="Standard Return – 7 days">
                        Standard Return – 7 days
                      </MenuItem>
                      <MenuItem value="Exchange Only – 7 days">
                        Exchange Only – 7 days
                      </MenuItem>
                      <MenuItem value="No Returns (personalized / hygiene)">
                        No Returns (personalized / hygiene)
                      </MenuItem>
                      <MenuItem value="custom">Custom</MenuItem>
                    </TextField>

                    {/* Custom Return Box */}
                    {DeliveryFormik.values.returnPolicyType === "custom" && (
                      <TextField
                        id="returnPolicyValue"
                        name="returnPolicyValue"
                        multiline
                        fullWidth
                        sx={{ mt: 2 }}
                        placeholder="Enter custom return conditions"
                        value={DeliveryFormik.values.returnPolicyValue}
                        onChange={(e) =>
                          DeliveryFormik.setFieldValue(
                            "returnPolicyValue",
                            e.target.value
                          )
                        }
                        onBlur={DeliveryFormik.handleBlur}
                        helperText={
                          DeliveryFormik.touched.returnPolicyValue &&
                          typeof DeliveryFormik.errors.returnPolicyValue ===
                            "string"
                            ? DeliveryFormik.errors.returnPolicyValue
                            : ""
                        }
                      />
                    )}
                  </div>

                  {/* Return Address */}
                  <div className="control_group w_100">
                    <label>
                      Return Address
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={DeliveryFormik.values.sameAsPickup}
                            onChange={(e) => {
                              const checked = e.target.checked;

                              DeliveryFormik.setFieldValue(
                                "sameAsPickup",
                                checked
                              );

                              if (checked) {
                                DeliveryFormik.setFieldValue(
                                  "returnAddress",
                                  vendorData?.shippingAddress?.detail || ""
                                );
                              } else {
                                DeliveryFormik.setFieldValue(
                                  "returnAddress",
                                  ""
                                );
                              }
                            }}
                          />
                        }
                        label="Same as Pickup Address"
                      />
                    </label>

                    <TextField
                      id="returnAddress"
                      name="returnAddress"
                      multiline
                      fullWidth
                      placeholder="Enter return address"
                      className="text_field"
                      value={DeliveryFormik.values.returnAddress}
                      onChange={(val) => {
                        if (
                          val.target.value === " " ||
                          val.target.value === "."
                        ) {
                        } else {
                          DeliveryFormik.handleChange(val);
                        }
                      }}
                      onBlur={DeliveryFormik.handleBlur}
                      helperText={
                        DeliveryFormik.touched.returnAddress &&
                        typeof DeliveryFormik.errors.returnAddress === "string"
                          ? DeliveryFormik.errors.returnAddress
                          : ""
                      }
                    />
                  </div>
                </div>
              </Box>
            </div>

            <div className="btn_flex">
              <Button
                className="btn btn_grey"
                onClick={handleBack}
                disabled={activeStep === 0}
              >
                Back
              </Button>

              <Button
                className="btn btn_primary"
                type="submit"
                onClick={() => setError(true)}
              >
                Next
              </Button>
            </div>
          </form>
        );
      case 4:
        return (
          <form className="form" onSubmit={BrandFormik.handleSubmit}>
            <div className="gap_p">
              {/* ------------------- STORE BANNER ------------------- */}
              <div className="control_group wrap_viewDemo_steps">
                <label>Store Banner</label>
                {banner ? (
                  <div>
                    <div style={{ position: "relative" }}>
                      <figure>
                        <CardMedia
                          style={{
                            height: "150px",
                            borderRadius: "12px",
                            objectFit: "cover",
                          }}
                          component="img"
                          image={banner}
                          alt="photo"
                        />
                      </figure>
                      <GridCloseIcon
                        style={{
                          color: "white",
                          position: "absolute",
                          right: 5,
                          top: 5,
                          backgroundColor: "red",
                          borderRadius: "50%",
                          cursor: "pointer",
                        }}
                        onClick={() => {
                          setBanner("");
                        }}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="upload_box">
                    <label
                      className="upload_action"
                      // onClick={() => setOpenModal(true)}
                    >
                      <p>
                        <Input
                          sx={{ display: "none" }}
                          id="icon-button-file"
                          type="file"
                          inputProps={{
                            accept: "image/png,image/jpeg",
                          }}
                          onChange={(e: any) => {
                            handleProfileUpload(e);
                            e.target.value = null;
                          }}
                        />
                        <img src="/static/images/upload_icon.svg" alt="icon" />
                        <strong>Upload Store Banner</strong>
                      </p>
                    </label>
                  </div>
                )}
              </div>

              {/* =================== STORE MOTTO =================== */}
              <div className="control_group ai_rec_btn1">
                <label>Store Motto</label>

                {/* Templates */}

                <Select fullWidth value={"default"} className="select_abs">
                  <MenuItem disabled value="default">
                    Select Motto
                  </MenuItem>
                  {storeMottoTemplates.map((motto, index) => (
                    <MenuItem value="All"> {motto}</MenuItem>
                  ))}
                </Select>

                {/* Custom Input (editable after selecting template OR clicking AI) */}
                <TextField
                  fullWidth
                  placeholder="Enter custom motto"
                  id="motto"
                  name="motto"
                  className="text_field"
                  value={BrandFormik.values.motto}
                  onChange={(val) => {
                    if (val.target.value === " " || val.target.value === ".") {
                    } else {
                      BrandFormik.handleChange(val);
                    }
                  }}
                  onBlur={BrandFormik.handleBlur}
                  helperText={
                    BrandFormik.touched.motto &&
                    typeof BrandFormik.errors.motto === "string"
                      ? BrandFormik.errors.motto
                      : ""
                  }
                />

                {/* AI Button */}
                <Button
                  className="btn btn_primary sm"
                  sx={{ mt: 2 }}
                  onClick={() => {
                    // Example AI logic
                    const aiGenerated =
                      "A modern celebration of UAE creativity.";
                    setSelectedMotto(aiGenerated);
                    setCustomMotto("");
                  }}
                >
                  <img
                    style={{ filter: "brightness(0) invert(1)" }}
                    src="/static/images/ai_icon.svg"
                    alt=""
                  />
                  Generate with AI
                </Button>
              </div>

              {/* =================== STORE STORY =================== */}
              <div className="control_group ai_rec_btn">
                <label>Store Story / About Your Brand</label>

                <Select fullWidth value={"default"} className="select_abs">
                  <MenuItem disabled value="default">
                    Select Template
                  </MenuItem>
                  {storeStoryTemplates.map((template, index) => (
                    <MenuItem value="All">{template}</MenuItem>
                  ))}
                </Select>
                <TextField
                  className="mt_20 text_field"
                  placeholder="Short Story"
                  hiddenLabel
                  fullWidth
                  multiline
                  id="story"
                  name="story"
                  value={BrandFormik.values.story}
                  onChange={(val) => {
                    if (val.target.value === " " || val.target.value === ".") {
                    } else {
                      BrandFormik.handleChange(val);
                    }
                  }}
                  onBlur={BrandFormik.handleBlur}
                  helperText={
                    BrandFormik.touched.story &&
                    typeof BrandFormik.errors.story === "string"
                      ? BrandFormik.errors.story
                      : ""
                  }
                />

                {/* AI Button */}
                <Button
                  className="btn btn_primary sm"
                  onClick={() => {
                    const aiStory =
                      "Our journey began with a mission to empower local creators in the UAE. Every product in our store reflects passion, craftsmanship, and cultural pride...";
                    setStoreStory(aiStory);
                    setShowStoryEditor(true);
                  }}
                >
                  <img
                    style={{ filter: "brightness(0) invert(1)" }}
                    src="/static/images/ai_icon.svg"
                    alt=""
                  />
                  Enhance with AI
                </Button>

                {/* Editable Text Field AFTER template/AI */}
                {showStoryEditor && (
                  <TextField
                    multiline
                    fullWidth
                    minRows={5}
                    placeholder="Write your store story…"
                    value={storeStory}
                    onChange={(e) => setStoreStory(e.target.value)}
                    sx={{ mt: 2 }}
                  />
                )}
              </div>
            </div>
            <div className="btn_flex">
              <Button
                className="btn btn_grey"
                onClick={handleBack}
                disabled={activeStep === 0}
              >
                Back
              </Button>

              <Button className="btn btn_primary" type="submit">
                Next
              </Button>
            </div>
          </form>
        );
      case 5:
        return (
          <div className="pricing-wrapper">
            <div className="ai_recommendation">
              <figure>
                <img src="/static/images/ai_icon.svg" alt="icon" />
              </figure>

              <h3>AI Recommendation</h3>
              <p>Pro subscription will be best as per your store.</p>
            </div>
            <div className="pricing-container">
              {/* Free Plan */}
              <div className="pricing-card special">
                <div className="icon">🎁</div>
                <h3>Free</h3>
                <h2>
                  AED 0 <span>forever</span>
                </h2>
                <ul>
                  <li>Up to 10 products</li>
                  <li>Basic analytics</li>
                  <li>Email support</li>
                  <li>Payout Speed 48h</li>
                  <li>Standard processing</li>
                </ul>
              </div>

              {/* Grow Plan */}

              {/* Pro Plan */}
              <div className="pricing-card">
                <div className="icon">💼</div>
                <p className="suggested">
                  <img src="/static/images/ai_icon.svg" alt="" />
                  AI Recommended
                </p>
                <h3>Pro</h3>
                <h2>
                  AED 249 <span>per month</span>
                </h2>
                <ul>
                  <li>Unlimited products</li>
                  <li>Full AI suite</li>
                  <li>Premium analytics</li>
                  <li>Dedicated account manager</li>
                  <li>Advanced marketing automation</li>
                  <li>Payout Speed 5d</li>
                  <li>Early access to features</li>
                </ul>
              </div>

              {/* Youth Plan */}
              <div className="pricing-card ">
                <div className="icon">🎁</div>
                <h3>Youth Innovators</h3>
                <h2>
                  Free <span>for under 18</span>
                </h2>
                <ul>
                  <li>All Pro features</li>
                  <li>Mentorship program</li>
                  <li>Educational resources</li>
                  <li>Payout Speed 10d</li>
                  <li>Special recognition badge</li>
                </ul>
              </div>
              {/* Pro Plan */}
              <div className="pricing-card">
                <div className="icon">💼</div>
                <h3>Pro</h3>
                <h2>
                  AED 249 <span>per month</span>
                </h2>
                <ul>
                  <li>Unlimited products</li>
                  <li>Full AI suite</li>
                  <li>Premium analytics</li>
                  <li>Dedicated account manager</li>
                  <li>Advanced marketing automation</li>
                  <li>Payout Speed 5d</li>
                  <li>Early access to features</li>
                </ul>
              </div>
              {/* Pro Plan */}
              <div className="pricing-card">
                <div className="icon">💼</div>
                <h3>Pro</h3>
                <h2>
                  AED 249 <span>per month</span>
                </h2>
                <ul>
                  <li>Unlimited products</li>
                  <li>Full AI suite</li>
                  <li>Premium analytics</li>
                  <li>Dedicated account manager</li>
                  <li>Advanced marketing automation</li>
                  <li>Payout Speed 5d</li>
                  <li>Early access to features</li>
                </ul>
              </div>
            </div>

            <div className="btn_flex">
              <Button
                className="btn btn_grey"
                onClick={handleBack}
                disabled={activeStep === 0}
              >
                Back
              </Button>

              <Button className="btn btn_primary" onClick={handleSubmit}>
                Submit
              </Button>
            </div>
          </div>
        );
      default:
        return <Typography>Unknown Step</Typography>;
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep((prev) => prev - 1);
    }
  };

  const handleReset = () => {
    setActiveStep(0);
    setIsOtpVerified(false);
  };

  const getProfileAPi = async () => {
    setLoading(true);
    try {
      const response = await getProfile({ id: parsedUserData?.id }).unwrap();
      setLoading(false);
      if (response.statusCode === 200) {
        setVendorData(response?.data || null);
        if (response?.data?.step === VENDOR_PROFILE_STEPS.BANK) {
          setActiveStep(1);
        } else if (response?.data?.step === VENDOR_PROFILE_STEPS?.POLICIES) {
          setActiveStep(2);
        } else if (response?.data?.step === VENDOR_PROFILE_STEPS.SHIPPING) {
          setActiveStep(3);
        } else if (response?.data?.step === VENDOR_PROFILE_STEPS.RETURN) {
          setActiveStep(4);
        } else if (response?.data?.step === VENDOR_PROFILE_STEPS.BRANDING) {
          setActiveStep(5);
        }
        setSelectedCommitments(response?.data?.customerTrustInfo || []);
        setSelectedInstruction(response?.data?.pickupInstructions || "");
        setDeliverySLA(
          response?.data?.deliverySLA
            ? String(response?.data?.deliverySLA)
            : "1"
        );
        setFastDeliveryEnabled(response?.data?.enableFastDelivery || false);
        setBanner(response?.data?.storeBanner || "");
        setPlaces(response?.data?.regionsAvailable || []);
      }
    } catch (error: any) {
      setLoading(false);
      showError(error?.data?.message || "");
    }
  };

  useEffect(() => {
    getProfileAPi();
  }, []);

  return (
    <>
      <Loader isLoad={loading} />
      <AuthLayout>
        <div className="authBox signup_box">
          <section className="wrap_viewDemo_steps uh_spc">
            <div className="container">
              <div className="page_head hd_3">
                <h2>{steps[activeStep]}</h2>
              </div>

              <div className="steps_grid">
                <div className="step_lt">
                  <Stepper activeStep={activeStep} alternativeLabel>
                    {steps.map((label, index) => {
                      const percentage = Math.round(
                        ((index + 1) / steps.length) * 100
                      );
                      return (
                        <Step key={index}>
                          <StepLabel>
                            <Box
                              display="flex"
                              flexDirection="column"
                              alignItems="center"
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {percentage}%
                              </Typography>
                            </Box>
                          </StepLabel>
                        </Step>
                      );
                    })}
                  </Stepper>
                </div>

                <div className="step_rt">
                  <div className="shadow_box shadow_box1">
                    {activeStep === steps.length ? (
                      <div className="completed_step">
                        <Typography>
                          All steps completed - you're finished!
                        </Typography>
                        <Button onClick={handleReset} sx={{ mt: 2 }}>
                          Reset
                        </Button>
                      </div>
                    ) : (
                      <>
                        {renderStepContent(activeStep)}

                        {/* <div className="btn_flex">
                          <Button
                            className="btn btn_grey"
                            onClick={handleBack}
                            disabled={activeStep === 0}
                          >
                            Back
                          </Button>

                          {activeStep === steps.length - 1 ? (
                            isOtpVerified ? (
                              <p>sds</p>
                            ) : (
                              <Button
                                className="btn btn_primary"
                                onClick={() => navigate("/dashboard")}
                              >
                                Submit
                              </Button>
                            )
                          ) : (
                            <Button
                              className="btn btn_primary"
                              onClick={handleNext}
                            >
                              Next
                            </Button>
                          )}
                        </div> */}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </AuthLayout>
      <AIStyles
        open={openAIModal}
        onClose={() => setOpenAIModal(false)}
        setOpen={setOpenAIModal}
      />
      <SelectUpload
        open={openModal}
        onClose={handleCloseModal}
        setOpen={setOpenModal}
      />
    </>
  );
};

export default SubscriptionForm;
